import {
  ApplicationConfig,
  EnvironmentInjector,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
  runInInjectionContext
} from '@angular/core';
import {
  PreloadAllModules,
  provideRouter,
  RouteReuseStrategy,
  withPreloading
} from '@angular/router';
import { appRoutes } from './app.routes';
import { IonicRouteStrategy, iosTransitionAnimation } from '@ionic/angular';
import { provideIonicAngular } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import * as allIcons from 'ionicons/icons';
import {
  HttpInterceptorFn,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi
} from '@angular/common/http';
import { ClientApiModule, ClientApiConfiguration } from '@incust-mobile/api';
import { environment } from '../environments/environment';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { LanguageService, PLATFORM_CONFIG, PlatformService } from '@incust-mobile/core';

addIcons(allIcons);

const acceptLanguageInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.url.includes('/i18n/')) {
    return next(req);
  }

  const languageService = inject(LanguageService);
  const lang = languageService.currentLang() || 'en';

  const clonedReq = req.clone({
    setHeaders: {
      'Accept-Language': lang
    }
  });

  return next(clonedReq);
};

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    provideIonicAngular({
      mode: 'ios',
      swipeBackEnabled: true,
      navAnimation: iosTransitionAnimation
    }),
    provideRouter(appRoutes, withPreloading(PreloadAllModules)),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideHttpClient(
      withInterceptorsFromDi(),
      withInterceptors([acceptLanguageInterceptor])
    ),
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: '/i18n/',
        suffix: '.json'
      }),
      fallbackLang: 'en',
      lang: 'en'
    }),
    {
      provide: PLATFORM_CONFIG,
      useValue: { platformUrl: environment.apiConfigs.PLATFORM_URL }
    },
    importProvidersFrom(
      ClientApiModule.forRoot(
        () =>
          new ClientApiConfiguration({
            basePath: environment.apiConfigs.API_URL
          })
      )
    ),
    provideAppInitializer(() => {
      const env = inject(EnvironmentInjector);

      return runInInjectionContext(env, async () => {
        const platform = inject(PlatformService);
        const languageService = inject(LanguageService);
        await platform.init();

        const params = platform.platformParams();

        if (params) {
          languageService.init(
            params.supported_languages,
          );
        }
      });
    })
  ]
};
